import { useEffect, useId, useRef } from 'react';
import { cn } from '~/lib/utils';
import styles from './MorphingWord.module.css';

/**
 * "Morphing Text" (sv-animations / Magic UI), ported verbatim for one inline word in a heading.
 *
 * The loop, the timing and the style math are the reference component's, line for line:
 * `morphTime = 1.5`, `cooldownTime = 0.5`, blur `8 / fraction − 8` capped at 100, opacity
 * `fraction ^ 0.4`, both copies re-written every frame, `filter: none` while holding. The only
 * addition is the slot: the reference morphs a full-width centred block; here the box is sized ONCE
 * to the widest word and anchored left, so the words before it never move and each word grows out to
 * the right. A slot that followed the word's width recentred a centred line every cycle, which read
 * as the sentence bumping; the median balance below is what keeps a centred line looking centred.
 *
 * Reduced motion: the first word, still, no loop.
 */
export interface MorphingWordProps {
  texts: readonly string[];
  className?: string;
  /** Reference: 1.5 */
  morphTime?: number;
  /** Reference: 0.5 */
  cooldownTime?: number;
}

export function MorphingWord({
  texts,
  className,
  morphTime = 1.5,
  cooldownTime = 0.5,
}: MorphingWordProps) {
  const filterId = `threshold-${useId().replace(/:/g, '')}`;
  const slotRef = useRef<HTMLSpanElement | null>(null);
  const stageRef = useRef<HTMLSpanElement | null>(null);
  const text1Ref = useRef<HTMLSpanElement | null>(null);
  const text2Ref = useRef<HTMLSpanElement | null>(null);
  const sizerRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    const slot = slotRef.current;
    const stage = stageRef.current;
    const text1 = text1Ref.current;
    const text2 = text2Ref.current;
    const sizer = sizerRef.current;
    if (!slot || !stage || !text1 || !text2 || !sizer || texts.length === 0) return;

    // Sized ONCE, to the widest word, and never touched again: the slot so the sentence holds still,
    // the filtered box so the threshold filter never has to re-rasterise a moving target.
    let widest = 0;
    for (const word of texts) {
      sizer.textContent = word;
      widest = Math.max(widest, sizer.offsetWidth);
    }
    slot.style.width = `${Math.ceil(widest)}px`;
    stage.style.width = `${Math.ceil(widest)}px`;

    // Balance, for a centred line. A slot as wide as the longest word parks every shorter word
    // left of centre. Laying the slot out at the MEDIAN word's width (the overhang is a negative
    // margin; the box itself is unchanged) puts the typical word dead centre, with the longest
    // hanging a little right and the shortest a little left — and still nothing ever moves.
    const widths = texts.map((word) => {
      sizer.textContent = word;
      return sizer.offsetWidth;
    });
    sizer.textContent = texts[0] ?? '';
    const median = [...widths].sort((a, b) => a - b)[Math.floor(widths.length / 2)] ?? widest;
    slot.style.marginRight = `${-Math.ceil(widest - median)}px`;

    // The reference is tuned at 96px (`lg:text-[6rem]`): an 8px blur is 8% of a glyph there. At this
    // heading's size the same 8px dissolves the letters much further, so the blur is scaled to keep
    // the melt the same *relative* to the type it is melting.
    const blurScale = parseFloat(getComputedStyle(slot).fontSize) / 96 || 1;

    const still = window.matchMedia('(prefers-reduced-motion: reduce)');

    // First paint, and the whole story under reduced motion: word one, solid.
    text1.textContent = texts[0] ?? '';
    text2.textContent = texts[1 % texts.length] ?? '';
    text1.style.filter = 'none';
    text1.style.opacity = '100%';
    text2.style.filter = 'none';
    text2.style.opacity = '0%';

    if (still.matches || texts.length < 2) return;

    // ── the reference, verbatim ──────────────────────────────────────────────────────────────
    let textIndex = 0;
    let morph = 0;
    let cooldown = 0;
    let time = new Date();
    let animationFrameId: number | null = null;

    const setStyles = (fraction: number) => {
      text2.style.filter = `blur(${Math.min(8 / fraction - 8, 100) * blurScale}px)`;
      text2.style.opacity = `${Math.pow(fraction, 0.4) * 100}%`;

      const invertedFraction = 1 - fraction;
      text1.style.filter = `blur(${Math.min(8 / invertedFraction - 8, 100) * blurScale}px)`;
      text1.style.opacity = `${Math.pow(invertedFraction, 0.4) * 100}%`;

      text1.textContent = texts[textIndex % texts.length] ?? '';
      text2.textContent = texts[(textIndex + 1) % texts.length] ?? '';
    };

    const doMorph = () => {
      morph -= cooldown;
      cooldown = 0;

      let fraction = morph / morphTime;

      if (fraction > 1) {
        cooldown = cooldownTime;
        fraction = 1;
      }

      setStyles(fraction);

      if (fraction === 1) {
        textIndex++;
      }
    };

    const doCooldown = () => {
      morph = 0;
      text2.style.filter = 'none';
      text2.style.opacity = '100%';
      text1.style.filter = 'none';
      text1.style.opacity = '0%';
    };

    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);

      const newTime = new Date();
      const dt = (newTime.getTime() - time.getTime()) / 1000;
      time = newTime;

      cooldown -= dt;

      if (cooldown <= 0) {
        doMorph();
      } else {
        doCooldown();
      }
    };

    animate();

    return () => {
      if (animationFrameId !== null) cancelAnimationFrame(animationFrameId);
    };
  }, [texts, morphTime, cooldownTime]);

  return (
    <span
      ref={slotRef}
      className={cn(styles.slot, className)}
      aria-hidden="true"
    >
      {/* The reference's filtered box: fixed size, two copies stacked, threshold + 0.6px blur. */}
      <span ref={stageRef} className={styles.stage} style={{ filter: `url(#${filterId}) blur(0.6px)` }}>
        <span ref={text1Ref} className={styles.text} />
        <span ref={text2Ref} className={styles.text} />
      </span>
      {/* In flow and invisible: gives the slot its line box and is what its width is measured from. */}
      <span ref={sizerRef} className={styles.sizer} />
      <svg className={styles.filters} preserveAspectRatio="xMidYMid slice" aria-hidden="true">
        <defs>
          <filter id={filterId}>
            <feColorMatrix
              in="SourceGraphic"
              type="matrix"
              values="1 0 0 0 0
                      0 1 0 0 0
                      0 0 1 0 0
                      0 0 0 255 -140"
            />
          </filter>
        </defs>
      </svg>
    </span>
  );
}

MorphingWord.displayName = 'MorphingWord';
